import React, { useState } from 'react';
import {
    Box,
    Button,
    CardContent,
    CardHeader,
    Typography,
    Alert,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Divider
} from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import ErrorBoundary from '../../components/ErrorBoundary';

const ProjectEnvironmentSimple = ({ userToken, projectName }) => {
    const [settings, setSettings] = useState({
        python_version: '3.9',
        template: 'basic',
        memory_limit: '4G',
        cpu_limit: 2,
        custom_packages: []
    });
    const [newPackage, setNewPackage] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleCreateSettings = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://datahub.n2nai.io/api/v1/jupyterhub/project-environment/settings', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('user_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_token: userToken,
                    project_name: projectName,
                    ...settings
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create settings');
            }

            const result = await response.json();
            setSuccess('Environment settings created successfully!');
            setError(null);
            console.log('Settings created:', result);
        } catch (error) {
            setError(error.message);
            setSuccess(null);
        } finally {
            setLoading(false);
        }
    };

    const handleGetSettings = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `https://datahub.n2nai.io/api/v1/jupyterhub/project-environment/settings?user_token=${userToken}&project_name=${projectName}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('user_token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to get settings');
            }

            const result = await response.json();
            if (result.has_settings && result.settings) {
                setSettings({
                    python_version: result.settings.python_version || '3.9',
                    template: result.settings.environment_template || 'basic',
                    memory_limit: result.settings.memory_limit || '4G',
                    cpu_limit: result.settings.cpu_limit || 2,
                    custom_packages: result.settings.custom_packages || []
                });
                setSuccess('Environment settings loaded successfully!');
            } else {
                setSuccess('No existing settings found for this project');
            }
            setError(null);
        } catch (error) {
            setError(error.message);
            setSuccess(null);
        } finally {
            setLoading(false);
        }
    };

    const handleAddPackage = () => {
        if (newPackage.trim() && !settings.custom_packages.includes(newPackage.trim())) {
            setSettings({
                ...settings,
                custom_packages: [...settings.custom_packages, newPackage.trim()]
            });
            setNewPackage('');
        }
    };

    const handleRemovePackage = (packageToRemove) => {
        setSettings({
            ...settings,
            custom_packages: settings.custom_packages.filter((pkg) => pkg !== packageToRemove)
        });
    };

    return (
        <ErrorBoundary>
            <Box sx={{ p: 2 }}>
                <MainCard>
                    <CardHeader
                        title={`Project Environment Settings - ${projectName}`}
                        subheader="Configure Python environment for this project"
                    />
                    <CardContent>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            {/* Python Version */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Python Version</InputLabel>
                                    <Select
                                        value={settings.python_version}
                                        onChange={(e) => setSettings({ ...settings, python_version: e.target.value })}
                                        label="Python Version"
                                    >
                                        <MenuItem value="3.8">Python 3.8</MenuItem>
                                        <MenuItem value="3.9">Python 3.9</MenuItem>
                                        <MenuItem value="3.10">Python 3.10</MenuItem>
                                        <MenuItem value="3.11">Python 3.11</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Template */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Environment Template</InputLabel>
                                    <Select
                                        value={settings.template}
                                        onChange={(e) => setSettings({ ...settings, template: e.target.value })}
                                        label="Environment Template"
                                    >
                                        <MenuItem value="basic">Basic</MenuItem>
                                        <MenuItem value="data_science">Data Science</MenuItem>
                                        <MenuItem value="ml">Machine Learning</MenuItem>
                                        <MenuItem value="web">Web Development</MenuItem>
                                        <MenuItem value="research">Research</MenuItem>
                                        <MenuItem value="custom">Custom</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Memory Limit */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Memory Limit</InputLabel>
                                    <Select
                                        value={settings.memory_limit}
                                        onChange={(e) => setSettings({ ...settings, memory_limit: e.target.value })}
                                        label="Memory Limit"
                                    >
                                        <MenuItem value="2G">2GB</MenuItem>
                                        <MenuItem value="4G">4GB</MenuItem>
                                        <MenuItem value="8G">8GB</MenuItem>
                                        <MenuItem value="16G">16GB</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* CPU Limit */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>CPU Limit</InputLabel>
                                    <Select
                                        value={settings.cpu_limit}
                                        onChange={(e) => setSettings({ ...settings, cpu_limit: e.target.value })}
                                        label="CPU Limit"
                                    >
                                        <MenuItem value={1}>1 Core</MenuItem>
                                        <MenuItem value={2}>2 Cores</MenuItem>
                                        <MenuItem value={4}>4 Cores</MenuItem>
                                        <MenuItem value={8}>8 Cores</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Custom Packages */}
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Custom Packages
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Add Package"
                                        value={newPackage}
                                        onChange={(e) => setNewPackage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddPackage()}
                                        placeholder="e.g., numpy, pandas, matplotlib"
                                    />
                                    <Button variant="contained" onClick={handleAddPackage} disabled={!newPackage.trim()}>
                                        Add
                                    </Button>
                                </Box>

                                {settings.custom_packages.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {settings.custom_packages.map((pkg, index) => (
                                            <Chip
                                                key={index}
                                                label={pkg}
                                                onDelete={() => handleRemovePackage(pkg)}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button variant="contained" onClick={handleCreateSettings} disabled={loading} color="primary">
                                        {loading ? 'Creating...' : 'Create Settings'}
                                    </Button>

                                    <Button variant="outlined" onClick={handleGetSettings} disabled={loading} color="secondary">
                                        {loading ? 'Loading...' : 'Load Existing Settings'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </MainCard>
            </Box>
        </ErrorBoundary>
    );
};

export default ProjectEnvironmentSimple;
